import { Navigate } from "react-router-dom";
import { usePatientAuthStore } from "@/stores/patientAuthStore";
import { FamilyProvider } from "@/portals/patient/context/FamilyContext";
import { ImagingStudiesProvider } from "@/portals/patient/context/ImagingStudiesContext";
import { PatientDataProvider } from "@/portals/patient/context/PatientDataContext";
import { PatientProfileProvider } from "@/portals/patient/context/PatientProfileContext";
import { UploadHistoryProvider } from "@/portals/patient/context/UploadHistoryContext";
import { PatientLayout } from "@/portals/patient/layout/PatientLayout";
import { useIdleLogout } from "@/portals/patient/hooks/useIdleLogout";
import { PageSpinner } from "@/shared/components/spinner";
import { AppQueryProvider } from "@/lib/query/AppQueryProvider";
import type { UserProfile } from "@/shared/types";

interface PatientProtectedLayoutProps {
  profile: UserProfile;
}

function SessionLoader() {
  return (
    <div className="bg-white">
      <PageSpinner fullScreen />
    </div>
  );
}

export function PatientProtectedLayout({ profile }: PatientProtectedLayoutProps) {
  const isAuthenticated = usePatientAuthStore((s) => s.isAuthenticated);
  const isLoading = usePatientAuthStore((s) => s.isLoading);
  useIdleLogout();

  if (isLoading) return <SessionLoader />;
  if (!isAuthenticated) return <Navigate to="/patient/auth/signin" replace />;

  // Server-data cache for this session only — unmounted (and emptied) on logout.
  return (
    <AppQueryProvider>
      <PatientProfileProvider fallbackProfile={profile}>
        <PatientDataProvider>
          <FamilyProvider>
            <UploadHistoryProvider>
              <ImagingStudiesProvider>
                <PatientLayout />
              </ImagingStudiesProvider>
            </UploadHistoryProvider>
          </FamilyProvider>
        </PatientDataProvider>
      </PatientProfileProvider>
    </AppQueryProvider>
  );
}
